// components/DynamicLayout.js

import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  AppBar,
  Toolbar,
  IconButton,
  Button,
  Menu,
  MenuItem,
  Drawer,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Badge,
  Divider,
} from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import MenuIcon from "@mui/icons-material/Menu";
import LogoutIcon from "@mui/icons-material/Logout";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import { useNavigate, useLocation } from "react-router-dom";
import { API_URL } from "./config";
import GlobalChatbotWidget from "./chat/GlobalChatbotWidget";

const DynamicLayout = ({ children }) => {
  const navigate = useNavigate();

  const [menus, setMenus] = useState([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [subMenuOpen, setSubMenuOpen] = useState({});

  const [notifAnchorEl, setNotifAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);

  const token = localStorage.getItem("auth");

  const location = useLocation();
  const currentPath = location.pathname;

  const isActivePath = (path) => path && currentPath === path;

  const menuHasActivePage = (menu) =>
    menu.items?.some((item) => {
      if (isActivePath(item.path)) return true;
      return item.children?.some((child) => isActivePath(child.path));
    });
  
  const itemHasActiveChild = (item) =>
    item.children?.some((child) => isActivePath(child.path));

  const totalNotif = useMemo(
    () => notifications.reduce((sum, n) => sum + Number(n.Number || 0), 0),
    [notifications]
  );

  const toggleSubMenu = (key) => {
    setSubMenuOpen((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const authHeader = {
    headers: { Authorization: `Bearer ${token}` },
  };

  const fetchMenu = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/access/me/menu`, authHeader);
      setMenus(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("menu load error", err);
      setMenus([]);
    }
  };

  const fetchNotifications = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/notifications/summary`, authHeader);
      setNotifications(Array.isArray(res.data) ? res.data : []);
    } catch {
      setNotifications([]);
    }
  };

  useEffect(() => {
    if (!token) return;
    fetchMenu();
    fetchNotifications();

    const t = setInterval(fetchNotifications, 60000);
    return () => clearInterval(t);
  }, [token]);

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, authHeader);
    } catch {}

    localStorage.removeItem("auth");
    navigate("/");
  };

  const go = (path) => {
    navigate(path);
    setAnchorEl(null);
    setActiveDropdown(null);
    setMenuOpen(false);
  };

  const openDropdown = (event, moduleCode) => {
    setAnchorEl(event.currentTarget);
    setActiveDropdown(moduleCode);
    setSubMenuOpen({});
  };

  const notifRouteByType = (type) => {
    if (type === "TimeOffRequest Pending Approval") return "/ApproverTimeOffRequest";
    if (type === "Applications Pending Approval") return "/ApproverStudentApplication";
    if (type === "Applications Pending Verify") return "/ApproverVerifyStudentApplication";
    return "/home";
  };

  const menuPaperProps = {
    sx: {
      mt: 0,
      minWidth: 260,
      maxWidth: 360,
      maxHeight: "75vh",
      overflowY: "auto",
      backgroundImage: "linear-gradient(135deg, #1976D2, #0D47A1)",
      color: "white",
      borderRadius: 0,
      boxShadow: "0 8px 24px rgba(0,0,0,0.25)",
      border: "1px solid rgba(255,255,255,0.12)",
    },
  };

  const menuListProps = {
    sx: {
      p: 0.5,
    },
  };

  const menuItemSx = (pl = 1, active = false) => ({
    pl,
    pr: 1,
    py: 0.35,
    minHeight: 30,
    fontSize: ".85rem",
    lineHeight: 1.5,
    borderRadius: 0,
    mx: 0,
    backgroundColor: "transparent",
  
    "&:hover": {
      backgroundColor: "transparent",
    },
  
    "& .menuText": {
      color: active ? "#F07828" : "white",
      textDecoration: active ? "underline" : "none",
      textUnderlineOffset: "6px",
      textDecorationColor: "#F07828",
    },
  
    "&:hover .menuText": {
      color: "#F07828",
      textDecoration: "underline",
      textUnderlineOffset: "6px",
      textDecorationColor: "#F07828",
    },
  });  

  const mobileAccordionSx = (active = false) => ({
    backgroundImage: "linear-gradient(to left, #2196F3, #0D47A1)",
    color: "white",
    boxShadow: "none",
  
    "&:before": {
      display: "none",
    },
  
    "& .mobileHeaderText": {
      color: active ? "#F07828" : "white",
      textDecoration: active ? "underline" : "none",
      textUnderlineOffset: "6px",
      textDecorationColor: "#F07828",
    },
  
    "&:hover .mobileHeaderText": {
      color: "#F07828",
      textDecoration: "underline",
      textUnderlineOffset: "6px",
      textDecorationColor: "#F07828",
    },
  });

  const mobileMenuItemSx = (active = false) => ({
    color: "white",
    fontSize: "1rem",
    backgroundColor: "transparent",
  
    "&:hover": {
      backgroundColor: "transparent",
    },
  
    "& .menuText": {
      color: active ? "#F07828" : "white",
      textDecoration: active ? "underline" : "none",
      textUnderlineOffset: "6px",
      textDecorationColor: "#F07828",
    },
  
    "&:hover .menuText": {
      color: "#F07828",
      textDecoration: "underline",
      textUnderlineOffset: "6px",
      textDecorationColor: "#F07828",
    },
  });

  const hasChildren = (item) => Array.isArray(item.children) && item.children.length > 0;

  const safeGo = (path) => {
    if (!path || path.startsWith("#")) return;
    go(path);
  };

  return (
    <>
      <AppBar
        position="static"
        sx={{
          backgroundImage: "linear-gradient(to left, #2196F3, #0D47A1)",
          color: "white",
          maxHeight: "50px",
        }}
      >
        <Toolbar>
          <IconButton color="inherit" onClick={() => navigate("/home")}>
            <HomeIcon />
          </IconButton>

          <Box
            sx={{
              display: { xs: "none", md: "flex" },
              gap: 2,
              alignItems: "center",
              width: "100%",
            }}
          >
            <Box
              sx={{
                display: "flex",
                gap: 0.5,
                marginRight: "auto",
                height: "100%",
                alignItems: "center",
              }}
            >
              {menus.map((menu) => (
                <React.Fragment key={menu.moduleCode}>
                    {/*
                    <Button
                      color="inherit"
                      onClick={(e) => openDropdown(e, menu.moduleCode)}
                      sx={{
                        fontSize: ".8rem",
                        minHeight: 40,
                        px: 1.2,
                        color:
                          activeDropdown === menu.moduleCode || menuHasActivePage(menu)
                            ? "#F07828"
                            : "white",
                        borderBottom:
                          activeDropdown === menu.moduleCode || menuHasActivePage(menu)
                            ? "2px solid #F07828"
                            : "2px solid transparent",
                        borderRadius: 0,
                        textUnderlineOffset: "2px",
                        "&:hover": {
                          backgroundColor: "transparent",
                          textDecoration: "underline",
                          textUnderlineOffset: "2px",
                          textDecorationColor: "#F07828",
                          borderBottom: "2px solid rgba(240,120,40,0.5)",
                        },
                      }}
                    >
                      {menu.label}
                    </Button>
                    */}
                    <Button
                      color="inherit"
                      onClick={(e) => openDropdown(e, menu.moduleCode)}
                      sx={{
                        fontSize: ".8rem",
                        minHeight: 40,
                        px: 1.2,
                        borderRadius: 0,
                        backgroundColor: "transparent",
                    
                        "&:hover": {
                          backgroundColor: "transparent",
                        },
                    
                        "& .menuText": {
                          color:
                            activeDropdown === menu.moduleCode || menuHasActivePage(menu)
                              ? "#F07828"
                              : "white",
                          textDecoration:
                            activeDropdown === menu.moduleCode || menuHasActivePage(menu)
                              ? "underline"
                              : "none",
                          textUnderlineOffset: "6px", // increase space here
                          textDecorationColor: "#F07828",
                        },
                    
                        "&:hover .menuText": {
                          color: "#F07828",
                          textDecoration: "underline",
                          textUnderlineOffset: "6px", // increase space here
                          textDecorationColor: "#F07828",
                        },
                      }}
                    >
                      <span className="menuText">{menu.label}</span>
                    </Button>

                    <Menu
                      anchorEl={anchorEl}
                      open={activeDropdown === menu.moduleCode}
                      onClose={() => setActiveDropdown(null)}
                      PaperProps={menuPaperProps}
                      MenuListProps={{ sx: { p: 0 } }}
                      anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "left",
                      }}
                      transformOrigin={{
                        vertical: "top",
                        horizontal: "left",
                      }}
                    >
                    {menu.items.map((item) => {
                        if (hasChildren(item)) {
                          const groupKey = `${menu.moduleCode}-${item.pageCode}`;
                        
                          return (
                            <React.Fragment key={groupKey}>
                              <MenuItem
                                onClick={() => toggleSubMenu(groupKey)}
                                sx={{
                                  ...menuItemSx(1, itemHasActiveChild(item)),
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  //color: "#FFD699",
                                  //fontWeight: 700,
                                }}
                              >
                                {/*<span>{item.label}</span>*/}
                                <span className="menuText">{item.label}</span>
                            
                                <ExpandMoreIcon
                                  sx={{
                                    fontSize: "1rem",
                                    color: "#F07828",
                                    transform: subMenuOpen[groupKey] ? "rotate(180deg)" : "rotate(0deg)",
                                    transition: "transform 0.2s ease",
                                  }}
                                />
                              </MenuItem>
                              
                              {subMenuOpen[groupKey] &&
                                item.children.map((child) => (
                                  <MenuItem
                                    key={child.pageCode || child.path}
                                    onClick={() => safeGo(child.path)}
                                    //sx={{ ...menuItemSx(10), ml: 10 }}
                                    sx={menuItemSx(3, isActivePath(child.path))}
                                  >
                                    {/*{child.label}*/}
                                    <span className="menuText">{child.label}</span>
                                  </MenuItem>
                                ))}
                            </React.Fragment>
                          );
                        }
                  
                      return (
                        <MenuItem
                          key={item.pageCode || item.path}
                          onClick={() => safeGo(item.path)}
                          sx={menuItemSx(1, isActivePath(item.path))}
                        >
                          {/*{item.label}*/}
                          <span className="menuText">{item.label}</span>
                        </MenuItem>
                      );
                    })}
                  </Menu>
                </React.Fragment>
              ))}
            </Box>

            <IconButton color="inherit" onClick={(e) => setNotifAnchorEl(e.currentTarget)}>
              <Badge badgeContent={totalNotif} color="error">
                <NotificationsNoneIcon />
              </Badge>
            </IconButton>

            <Menu
              anchorEl={notifAnchorEl}
              open={Boolean(notifAnchorEl)}
              onClose={() => setNotifAnchorEl(null)}
            >
              {notifications.length === 0 ? (
                <MenuItem disabled>No pending notifications</MenuItem>
              ) : (
                notifications.map((n, idx) => (
                  <MenuItem
                    key={idx}
                    onClick={() => {
                      setNotifAnchorEl(null);
                      go(notifRouteByType(n.NotificationType));
                    }}
                  >
                    {n.NotificationType} — {Number(n.Number || 0)}
                  </MenuItem>
                ))
              )}

              <Divider />

              <MenuItem onClick={fetchNotifications}>Refresh</MenuItem>
            </Menu>

            <Button color="inherit" onClick={logout}>
              <LogoutIcon />
            </Button>
          </Box>

          <IconButton
            sx={{ display: { xs: "block", md: "none" }, ml: "auto" }}
            color="inherit"
            onClick={() => setMenuOpen(true)}
          >
            <MenuIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Drawer anchor="left" open={menuOpen} onClose={() => setMenuOpen(false)}>
        <Box
          sx={{
            width: 350,
            minHeight: "100vh",
            backgroundImage: "linear-gradient(to left, #2196F3, #0D47A1)",
            color: "white",
          }}
        >
          {menus.map((menu) => {
            const key = menu.moduleCode;

            return (
              <Accordion
                key={key}
                sx={mobileAccordionSx(expanded === key || menuHasActivePage(menu))}
                expanded={expanded === key}
                onChange={() => setExpanded(expanded === key ? null : key)}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: "white" }} />}>
                  <Typography className="mobileHeaderText" sx={{ fontSize: "1.3rem" }}> {menu.label} </Typography>
                </AccordionSummary>

                <AccordionDetails sx={{ p: 0 }}>
                    {menu.items.map((item) => {
                        if (hasChildren(item)) {
                          const groupKey = `${menu.moduleCode}-${item.pageCode}`;
                        
                          return (
                            <React.Fragment key={groupKey}>
                              <MenuItem
                                onClick={() => toggleSubMenu(groupKey)}
                                sx={{
                                  ...mobileMenuItemSx(itemHasActiveChild(item)),
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  pl: 2,
                                  //color: "#FFD699",
                                  //fontWeight: 700,
                                }}
                              >
                                {/*<span>{item.label}</span>*/}
                                <span className="menuText">{item.label}</span>
                            
                                <ExpandMoreIcon
                                  sx={{
                                    fontSize: "1rem",
                                    color: "#F07828",
                                    transform: subMenuOpen[groupKey] ? "rotate(180deg)" : "rotate(0deg)",
                                    transition: "transform 0.2s ease",
                                  }}
                                />
                              </MenuItem>
                              
                              {subMenuOpen[groupKey] &&
                                item.children.map((child) => (
                                  <MenuItem
                                    key={child.pageCode || child.path}
                                    onClick={() => safeGo(child.path)}
                                    sx={{ ...mobileMenuItemSx(isActivePath(child.path)), pl: 5 }}
                                  >
                                    {/*{child.label}*/}
                                    <span className="menuText">{child.label}</span>
                                  </MenuItem>
                                ))}
                            </React.Fragment>
                          );
                        }
                  
                      return (
                        <MenuItem
                          key={item.pageCode || item.path}
                          onClick={() => safeGo(item.path)}
                          sx={mobileMenuItemSx(isActivePath(item.path))}
                        >
                          {/*{item.label}*/}
                          <span className="menuText">{item.label}</span>
                        </MenuItem>
                      );
                    })}
                </AccordionDetails>
              </Accordion>
            );
          })}

          <MenuItem onClick={logout} sx={{ color: "white" }}>
            <LogoutIcon sx={{ mr: 1 }} />
            Logout
          </MenuItem>
        </Box>
      </Drawer>

      <main style={{ padding: "16px" }}>{children}</main>

      <GlobalChatbotWidget />
    </>
  );
};

export default DynamicLayout;